// @services/stubs/StubAuthenticationClient.ts
import type { AuthenticationClient } from '@services/clients/AuthenticationClient';
import type { AuthenticationError, CreateAccountRequest, JwtResponse, LoginRequest, PasswordResetCompleteRequest, PasswordResetError, PasswordResetStartRequest } from "@ps-bridge/models/core/dataContracts";
import type { EffectTs, OptionTs } from '@ps-bridge/interop';

import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import { makeJwtStatus } from "@ps-bridge/models/brands/jwtStatus";
import { makeJwtSubscriptionStatus } from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import { makeErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import { Duration, Effect } from 'effect';
import { effectFromRaw } from '@utils/effects/branded';
import { effectFail } from '@utils/effects/branded';
import * as Option from "@utils/Option";

const delaySuccess = <A>(value: A): Effect.Effect<A> =>
    Effect.flatMap(Effect.sleep(Duration.millis(100)), () => Effect.succeed(value));

const STUB_AUTH_MODE_KEY = "stub:auth:mode";
const STUB_AUTH_ERROR_MESSAGE_KEY = "stub:auth:errorMessage";
const STUB_AUTH_ERROR_TYPE_KEY = "stub:auth:errorType";
const STUB_AUTH_LOGIN_STATUS_KEY = "stub:auth:loginStatus";
const STUB_AUTH_ERROR_TYPE = "NetworkError";
const DEFAULT_ERROR_MESSAGE = "Authentication failed.";
const DEFAULT_PASSWORD_RESET_ERROR_MESSAGE = "Unable to request password reset.";
const DEFAULT_LOGIN_STATUS = "PaymentNotYetConfigured";

const getStubStorageValue = (key: string): OptionTs<string> => {
    if (typeof window === "undefined") {
        return Option.none();
    }
    try {
        const value = window.localStorage.getItem(key);
        return typeof value === "string" ? Option.some(value) : Option.none();
    } catch (error) {
        console.warn("StubAuthenticationClient failed to read localStorage", error);
        return Option.none();
    }
};

const getStubAuthMode = (): "success" | "error" =>
    Option.fold(
        getStubStorageValue(STUB_AUTH_MODE_KEY),
        () => "success",
        (value) => (value === "error" ? "error" : "success"),
    );

const buildAuthError = (): AuthenticationError => ({
    type: Option.fold(
        getStubStorageValue(STUB_AUTH_ERROR_TYPE_KEY),
        () => STUB_AUTH_ERROR_TYPE,
        (value) => value,
    ),
    message: makeErrorMessageText(
        Option.fold(
            getStubStorageValue(STUB_AUTH_ERROR_MESSAGE_KEY),
            () => DEFAULT_ERROR_MESSAGE,
            (value) => value,
        ),
    ),
    statusCode: Option.none(),
});

const buildPasswordResetError = (): PasswordResetError => ({
    type: Option.fold(
        getStubStorageValue(STUB_AUTH_ERROR_TYPE_KEY),
        () => STUB_AUTH_ERROR_TYPE,
        (value) => value,
    ),
    message: makeErrorMessageText(
        Option.fold(
            getStubStorageValue(STUB_AUTH_ERROR_MESSAGE_KEY),
            () => DEFAULT_PASSWORD_RESET_ERROR_MESSAGE,
            (value) => value,
        ),
    ),
    statusCode: Option.none(),
});

const getStubLoginStatus = (): string =>
    Option.fold(
        getStubStorageValue(STUB_AUTH_LOGIN_STATUS_KEY),
        () => DEFAULT_LOGIN_STATUS,
        (value) => value,
    );

export class StubAuthenticationClient implements AuthenticationClient {
    createAccount(request: CreateAccountRequest): EffectTs<Ok, AuthenticationError> {
        console.log('StubAuthenticationClient.createAccount called', request);
        if (getStubAuthMode() === "error") {
            return effectFail(buildAuthError());
        }
        return effectFromRaw(delaySuccess(ok));
    }

    login(request: LoginRequest): EffectTs<string, AuthenticationError> {
        console.log('StubAuthenticationClient.login called', request);
        if (getStubAuthMode() === "error") {
            return effectFail(buildAuthError());
        }
        return effectFromRaw(delaySuccess(getStubLoginStatus()));
    }

    refreshJwtToken: EffectTs<JwtResponse, AuthenticationError> = effectFromRaw(delaySuccess({
        status: makeJwtStatus('authorized'),
        subscription_status: makeJwtSubscriptionStatus('FreeTrialMode'),
    } as JwtResponse));

    logout: EffectTs<Ok, AuthenticationError> = effectFromRaw(delaySuccess(ok));

    requestPasswordReset(request: PasswordResetStartRequest): EffectTs<Ok, PasswordResetError> {
        console.log('StubAuthenticationClient.requestPasswordReset called', request);
        if (getStubAuthMode() === "error") {
            return effectFail(buildPasswordResetError());
        }
        return effectFromRaw(delaySuccess(ok));
    }

    completePasswordReset(request: PasswordResetCompleteRequest): EffectTs<Ok, PasswordResetError> {
        console.log('StubAuthenticationClient.completePasswordReset called', request);
        if (getStubAuthMode() === "error") {
            return effectFail(buildPasswordResetError());
        }
        return effectFromRaw(delaySuccess(ok));
    }
}
